<template>
  <div class="kiosk-wait">
    <img src="/elite-logo-white.png" alt="Elite Fitness" class="kw-logo" />

    <!-- In attesa che il socio inquadri e completi dal telefono -->
    <template v-if="phase === 'waiting'">
      <h1 class="kw-title">Registrati dal tuo telefono</h1>
      <p class="kw-lead">
        Inquadra il codice con la fotocamera: completi la registrazione sul telefono,
        poi torni qui per pagare.
      </p>

      <div class="kw-qr">
        <img v-if="qrDataUrl" :src="qrDataUrl" alt="Codice QR per la registrazione" />
        <div v-else class="kw-qr-loading">Preparazione del codice…</div>
      </div>

      <p class="kw-status" :class="{ 'kw-status--live': claimed }">
        <span v-if="claimed">Registrazione in corso sul telefono…</span>
        <span v-else>In attesa della scansione…</span>
      </p>

      <button type="button" class="kw-cancel" @click="cancel">Annulla</button>
    </template>

    <!-- Esito negativo: sempre con una via d'uscita esplicita -->
    <template v-else>
      <h1 class="kw-title">{{ errorTitle }}</h1>
      <p class="kw-lead">{{ errorText }}</p>
      <button type="button" class="kw-retry" @click="restart">Riprova</button>
      <button type="button" class="kw-cancel" @click="cancel">Torna indietro</button>
    </template>
  </div>
</template>

<script>
import QRCode from 'qrcode';
import { API_CONFIG } from '@/config/api';

const MEMBER_APP_URL = 'https://app.elitefitness.it';

/**
 * Polling a scaglioni: il device-login si risolve in pochi secondi, la
 * registrazione in molti minuti. Un intervallo fisso di 2 s per tutta l'attesa
 * genererebbe centinaia di richieste per singola registrazione.
 */
const POLL_STEPS = [
  { untilMs: 30000, everyMs: 2000 },
  { untilMs: 180000, everyMs: 5000 },
];
const POLL_FALLBACK_MS = 10000;

/**
 * Margine oltre la scadenza della sessione prima di riportare il totem alla
 * home: l'esenzione dall'idle timeout non deve mai diventare un blocco
 * permanente su un dispositivo non presidiato.
 */
const GRACE_MS = 60000;

/** Guard usato finché non si conosce la scadenza reale della sessione. */
const DEFAULT_GUARD_MS = 20 * 60 * 1000 + GRACE_MS;

/** Su una schermata di errore nessuno sta più compilando: si rientra prima. */
const ERROR_GUARD_MS = 90000;

/** Il profilo serve solo a completare lo stato locale: non vale un'attesa lunga. */
const PROFILE_FETCH_TIMEOUT_MS = 8000;

export default {
  name: 'KioskWaitPage',
  data() {
    return {
      phase: 'waiting', // waiting | error
      qrDataUrl: null,
      sessionId: null,
      claimed: false,
      errorTitle: '',
      errorText: '',
      pollTimer: null,
      guardTimer: null,
      startedAt: 0,
      /**
       * Le continuazioni dopo un await possono riprendere quando il componente
       * è già stato smontato (es. un altro cliente preme Annulla mentre una
       * fetch è in volo). Senza questa guardia riarmerebbero timer non più
       * cancellabili da nessuno, e un poll superstite potrebbe scrivere il token
       * di un socio su un totem tornato ad altri usi.
       */
      destroyed: false,
    };
  },
  mounted() {
    this.start();
  },
  beforeUnmount() {
    this.destroyed = true;
    this.stopTimers();
  },
  methods: {
    async start() {
      this.stopTimers();
      this.phase = 'waiting';
      this.claimed = false;
      this.qrDataUrl = null;
      this.startedAt = Date.now();

      // Il guard parte SUBITO, prima di qualsiasi chiamata di rete: questa
      // pagina è esente dall'idle timeout globale, quindi ogni percorso che non
      // lo arma (errore di rete incluso) lascerebbe il totem bloccato qui.
      this.scheduleGuard(0);

      try {
        const res = await fetch(`${API_CONFIG.BASE_URL}/api/kiosk/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            purpose: 'REGISTRATION',
            productId: this.$route.query.productId || undefined,
            // Il flusso di registrazione della member app pretende una variante
            // non vuota; al totem spesso non c'è (i servizi linkano il solo
            // productId). Il valore non incide sul pagamento, che avviene qui.
            variationName: this.$route.query.variationName || 'default',
            centerKey: this.$route.query.centerKey || undefined,
            promoCode: this.$route.query.promoCode || undefined,
          }),
        });
        if (!res.ok) throw new Error('session');
        const { sessionId, code, expiresIn } = await res.json();
        if (this.destroyed) return;

        this.sessionId = sessionId;
        this.qrDataUrl = await QRCode.toDataURL(
          `${MEMBER_APP_URL}/kiosk-register?code=${encodeURIComponent(code)}`,
          { width: 320, margin: 1 },
        );

        this.scheduleGuard(expiresIn);
        this.scheduleNextPoll();
      } catch (e) {
        if (this.destroyed) return;
        this.fail('Non riesco a generare il codice', 'Controlla la connessione e riprova, oppure rivolgiti alla reception.');
      }
    },

    /**
     * Timer di sicurezza proprio: questa pagina è esente dall'idle timeout
     * globale, quindi deve garantire da sé che il totem torni sempre alla home.
     * Va (ri)armato a ogni cambio di schermata, errori compresi.
     */
    scheduleGuard(expiresInEpochMs) {
      if (this.guardTimer) clearTimeout(this.guardTimer);
      const ms = Number(expiresInEpochMs) > 0
        ? Math.max(0, Number(expiresInEpochMs) - Date.now()) + GRACE_MS
        : DEFAULT_GUARD_MS;
      this.guardTimer = setTimeout(() => {
        this.stopTimers();
        this.$router.push({ path: '/kiosk' });
      }, ms);
    },

    scheduleNextPoll() {
      const elapsed = Date.now() - this.startedAt;
      const stepped = POLL_STEPS.find((s) => elapsed < s.untilMs);
      const delay = stepped ? stepped.everyMs : POLL_FALLBACK_MS;
      this.pollTimer = setTimeout(this.poll, delay);
    },

    async poll() {
      if (!this.sessionId || this.destroyed) return;
      try {
        const r = await fetch(
          `${API_CONFIG.BASE_URL}/api/kiosk/auth/session/${encodeURIComponent(this.sessionId)}`,
        );
        const d = await r.json();
        if (this.destroyed) return;

        if (d.status === 'APPROVED') {
          // Si ferma solo il polling: il guard resta armato per coprire anche
          // onApproved, che fa una chiamata di rete prima di navigare via.
          this.stopPolling();
          await this.onApproved(d);
          return;
        }
        if (d.status === 'FAILED') {
          this.stopTimers();
          this.fail(
            'Registrazione non completata',
            d.failureReason
              ? `${d.failureReason} Puoi riprovare oppure farti aiutare in reception.`
              : 'La registrazione dal telefono non è andata a buon fine. Puoi riprovare oppure farti aiutare in reception.',
          );
          return;
        }
        if (d.status === 'EXPIRED') {
          this.stopTimers();
          this.fail('Codice scaduto', 'È passato troppo tempo. Genera un nuovo codice e riprova.');
          return;
        }
        this.claimed = d.status === 'CLAIMED';
      } catch (_) {
        /* rete: si riprova al tick successivo */
      }
      if (this.destroyed) return;
      this.scheduleNextPoll();
    },

    /**
     * Rientra nel checkout con il socio autenticato. Oltre al token in query
     * (che il checkout valida da solo), scrive anche userEmail in localStorage:
     * senza, un reload della pagina non riconoscerebbe più il socio come loggato.
     */
    async onApproved(d) {
      localStorage.setItem('userToken', d.token);
      localStorage.setItem('tokenExpiration', d.expiresIn);
      try {
        // Timeout esplicito: una fetch appesa (rete del totem degradata, proxy
        // che non chiude) bloccherebbe la navigazione e lascerebbe il totem su
        // questa pagina, che è esente dall'idle timeout globale.
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), PROFILE_FETCH_TIMEOUT_MS);
        try {
          const res = await fetch(`${API_CONFIG.BASE_URL}/api/users/profile`, {
            headers: { Authorization: `Bearer ${d.token}` },
            signal: ctrl.signal,
          });
          if (res.ok) {
            const profile = await res.json();
            if (profile?.email) localStorage.setItem('userEmail', profile.email);
          }
        } finally {
          clearTimeout(t);
        }
      } catch (_) {
        /* non bloccante: il checkout rivalida comunque il token dalla query */
      }
      if (this.destroyed) return;
      this.stopTimers();

      const returnUrlEncoded = this.$route.query.returnUrl;
      if (!returnUrlEncoded) {
        this.$router.push({ path: '/kiosk' });
        return;
      }
      const decoded = decodeURIComponent(returnUrlEncoded);
      const [pathOnly, queryString] = decoded.split('?');
      const params = new URLSearchParams(queryString || '');
      if (!params.has('userToken')) params.set('userToken', d.token);
      this.$router.push({ path: pathOnly, query: Object.fromEntries(params) });
    },

    fail(title, text) {
      this.phase = 'error';
      this.errorTitle = title;
      this.errorText = text;
      // Chi ha causato l'errore spesso se n'è già andato: il totem non deve
      // restare su questa schermata (esente dall'idle timeout) a tempo indefinito.
      this.stopTimers();
      this.guardTimer = setTimeout(() => {
        this.stopTimers();
        this.$router.push({ path: '/kiosk' });
      }, ERROR_GUARD_MS);
    },

    restart() {
      this.start();
    },

    cancel() {
      this.stopTimers();
      const returnUrlEncoded = this.$route.query.returnUrl;
      if (returnUrlEncoded) {
        const decoded = decodeURIComponent(returnUrlEncoded);
        const [pathOnly, queryString] = decoded.split('?');
        const params = new URLSearchParams(queryString || '');
        this.$router.push({ path: pathOnly, query: Object.fromEntries(params) });
      } else {
        this.$router.push({ path: '/kiosk' });
      }
    },

    stopPolling() {
      if (this.pollTimer) { clearTimeout(this.pollTimer); this.pollTimer = null; }
    },

    stopTimers() {
      this.stopPolling();
      if (this.guardTimer) { clearTimeout(this.guardTimer); this.guardTimer = null; }
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap');

.kiosk-wait {
  position: fixed;
  inset: 0;
  background: #2e348c;
  color: #fff;
  font-family: 'Montserrat', sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  padding: 32px;
  text-align: center;
}

.kw-logo { width: 190px; margin-bottom: 4px; }
.kw-title { font-size: 2.1rem; font-weight: 700; margin: 0; }
.kw-lead { font-size: 1.15rem; max-width: 620px; margin: 0; opacity: 0.9; line-height: 1.5; }

.kw-qr {
  background: #fff;
  border-radius: 18px;
  padding: 16px;
  width: 352px;
  height: 352px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.kw-qr img { width: 320px; height: 320px; display: block; }
.kw-qr-loading { color: #2e348c; font-weight: 600; }

.kw-status { font-size: 1.05rem; margin: 0; opacity: 0.85; }
.kw-status--live { color: #ffd54a; font-weight: 600; opacity: 1; }

.kw-cancel,
.kw-retry {
  font-family: inherit;
  font-size: 1.05rem;
  font-weight: 600;
  padding: 14px 34px;
  border-radius: 999px;
  cursor: pointer;
}
.kw-retry { background: #ffd54a; color: #2e348c; border: none; }
.kw-cancel { background: transparent; color: #fff; border: 2px solid rgba(255, 255, 255, 0.55); }
</style>
